import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, OnChanges, SimpleChanges, CUSTOM_ELEMENTS_SCHEMA, inject } from '@angular/core';
import { BaseInputDirective } from '../base-input.directive';
import { SelectListService } from '../../services/form-utility/select-list.service';
import { ISelectOptionGroup } from '../../models/select-option-group';
import { ISelectOption } from '../../models/select-option';
import { BehaviorSubject, fromEvent, merge, Observable, Subscription } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

@Component({
  selector: 'app-select-input',
  templateUrl: './select-input.component.html',
  styleUrls: ['./select-input.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    CdkTextareaAutosize,
  ],
})
export class SelectInputComponent
  extends BaseInputDirective
  implements OnInit, OnDestroy, OnChanges {
  private selectListService = inject(SelectListService);

  @Input() fetchData: Observable<ISelectOptionGroup[]>;
  @Input() nameFormatter: (obj: object) => string;
  floatLabel = 'auto';
  @Output() richChange = new EventEmitter();
  @Input() initialGroupFilter: string;
  @Input() showNullPlaceholder = true;
  @Input() overrideReadonlyValue: string;
  @Input() autoselectOnlyOption: boolean = false;
  @Input() readonlyIfOnlyOption: boolean = false;
  @Output() fetchDataError = new EventEmitter();
  readonlyInternal: boolean;

  optionGroups: ISelectOptionGroup[];
  filteredGroups: ISelectOptionGroup[] = [];
  flatOptions: ISelectOption[];
  hasOneGroup = false;
  currentSelection: string;
  private hasSelectList: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private initialValue: any;
  private changeSubscription: Subscription;
  connected: Subscription;

  @Input() multiple = false;

  showTrimmedText: boolean;
  noteIsLong: boolean;
  trimmedText: string;

  readonly DEFAULT_PLACEHOLDER = '-Select-';

  
 



  ngOnChanges(changes: SimpleChanges) {
    if (changes['readonly'] && !changes['readonly'].firstChange) {
      this.readonlyInternal = this.readonly;
    }
  }

  ngOnInit() {
    super.ngOnInit();

    // Track if network connection is present, and if not mark input as invalid to prevent getting around validation
    // when offline.
    const online = fromEvent(window, 'online').pipe(map(() => true));
    const offline = fromEvent(window, 'offline').pipe(map(() => false));
    this.connected = merge(online, offline).subscribe(conn => {
      if (conn) {
        this.setAsValid();
        this.loadOptions(); // reload options when connection re-established
      } else {
        this.setAsInvalid();
      }
    });

    this.initialValue = this.formControl.value;
    this.readonlyInternal = this.readonly;

    this.changeSubscription = this.formControl.valueChanges.subscribe(value => {
      if (this.flatOptions) {
        this.currentSelection = this.selectListService
          .getCurrentSelection(this.flatOptions, value)
          .map(x => x.name)
          .sort()
          .join(', ');
      }
    });

    if (!this.placeholder) {
      this.placeholder = this.DEFAULT_PLACEHOLDER;
    }
    this.floatLabel = 'always';

    this.loadOptions();
  }

  refreshOptions() {
    this.loadOptions();
  }

  protected loadOptions(suppressErrors: boolean = false) {
    const dataFetcher = this.fetchData || this.defaultDataFetcher(this.fgInfo?.optionList);
    if (!dataFetcher) {
      return;
    }

    dataFetcher
      .pipe(
        tap(response => {
          this.optionsChanged(response);
        }),
      )
      .subscribe(
        selectOptionGroups => {
          this.optionGroups = this.deepCopy(selectOptionGroups);
          this.flatOptions = this.selectListService.flattenOptions(this.optionGroups);
          this.currentSelection = this.selectListService
            .getCurrentSelection(this.flatOptions, this.formControl?.value)
            .map(x => x.name)
            .sort()
            .join(', ');

          if (this.currentSelection.length > 200) {
            this.noteIsLong = true;
            this.showTrimmedText = true;
            this.trimmedText = this.currentSelection.substr(0, 200) + '...';
          }

          // Filter out inactive and not-selected options
          this.calculateOptions();

          this.hasSelectList.next(true);
        },
        (error: HttpErrorResponse) => {
          if (!suppressErrors) {
            this.fetchDataError.emit();
          }

          // retry after several seconds if connection was lost
          if (error.status === 504) {
            setTimeout(() => {
              this.loadOptions(true);
            }, 5000);
          }
        },
      );
  }

  private setAsInvalid() {
    if (!this.formControl.errors || !this.formControl.errors['notValidOption']) {
      this.formControl.setErrors({ notValidOption: true });
    }
  }

  private setAsValid() {
    const currentErrors = this.formControl.errors;
    if (!!currentErrors && !!currentErrors['notValidOption']) {
      delete currentErrors.notValidOption;
      this.formControl.setErrors(currentErrors);
    }
  }

  private defaultDataFetcher(optionList: string) {
    return this.selectListService
      .getSelectList(optionList, this.nameFormatter)
      .pipe(first(response => response != null));
  }

  private calculateOptions(emitEvent: boolean = true): any {
    if (this.readonlyIfOnlyOption && !this.readonly) {
      this.readonlyInternal = false;
    }
    this.filteredGroups = [];
    this.hasOneGroup = false;
    if (this.optionGroups) {
      this.filteredGroups = this.selectListService.filterAndSortSelectOptionGroup(
        this.optionGroups,
        this.initialGroupFilter,
      );

      if (this.filteredGroups.length === 1) {
        const optionGroup = this.filteredGroups[0];
        this.hasOneGroup = true;

        if (
          this.initialValue != null &&
          optionGroup?.options?.find(opt => opt.value === this.initialValue)
        ) {
          this.formControl.setValue(this.initialValue);
          if (emitEvent) {
            this.onChange();
          }
        } else if (this.optionGroups[0]?.defaultValue) {
          this.formControl.setValue(this.optionGroups[0].defaultValue);
          if (emitEvent) {
            this.onChange();
          }
        } else if (optionGroup.defaultValue) {
          this.formControl.setValue(optionGroup.defaultValue);
          if (emitEvent) {
            this.onChange();
          }
        } else if (!!this.autoselectOnlyOption && optionGroup.options.length === 1) {
          this.formControl.setValue(optionGroup.options[0].value);
          if (this.readonlyIfOnlyOption) {
            this.readonlyInternal = true;
          }
          if (emitEvent) {
            this.onChange();
          }
        }
      } else {
        const optionGroup = this.filteredGroups.find(g =>
          g.options.find(o => o.value === this.initialValue),
        );
        if (optionGroup) {
          const option = optionGroup.options.find(o => o.value === this.initialValue);
          this.formControl.setValue(option.value);
          if (emitEvent) {
            this.onChange();
          }
        }
      }
    }
  }

  activateOption(value: any) {
    this.hasSelectList.pipe(first(hasList => hasList === true)).subscribe(() => {
      this.updateOption(value, true);
    });
  }

  deactivateOption(value: any, emitEvent: boolean = true) {
    this.hasSelectList.pipe(first(hasList => hasList === true)).subscribe(() => {
      this.updateOption(value, false, emitEvent);
    });
  }

  deactivateChildOption(value: any, parentId: any, parentCategoryId: any) {
    this.hasSelectList.pipe(first(hasList => hasList === true)).subscribe(() => {
      this.updateChildOption(value, parentId, parentCategoryId, true);
    });
  }

  filterChildOptions(value: any, parentCategoryId?: number) {
    this.flatOptions.forEach(option => {
      if (option.parentId !== value || option.parentCategoryId !== parentCategoryId) {
        this.deactivateChildOption(option.value, option.parentId, option.parentCategoryId);
      }
    });
  }

  private updateOption(value: any, isDeleted: boolean, emitEvent: boolean = true) {
    if (this.optionGroups) {
      for (const group of this.optionGroups) {
        if (group.options) {
          for (const option of group.options) {
            if (option.value === value) {
              option.isDeleted = !isDeleted;

              this.calculateOptions(emitEvent);

              return;
            }
          }
        }
      }
    }
  }

  private updateChildOption(value: any, parentId: any, parentCategoryId: any, isDeleted: boolean) {
    if (this.optionGroups) {
      for (const group of this.optionGroups) {
        if (group.options) {
          for (const option of group.options) {
            if (
              option.value === value &&
              option.parentId === parentId &&
              option.parentCategoryId === parentCategoryId
            ) {
              option.isDeleted = isDeleted;

              this.calculateOptions();

              return;
            }
          }
        }
      }
    }
  }

  onChange() {
    // TODO: Possible optimizations to prevent getCurrentSelection from calling twice (onChange and valueChanges)
    const selections = this.selectListService.getCurrentSelection(
      this.flatOptions,
      this.formControl.value,
    );
    this.currentSelection = selections
      .map(x => x.name)
      .sort()
      .join(', ');
    this.change.emit({ value: this.formControl.value, name: this.currentSelection });
    this.richChange.next(selections);

    // == is correct here. Testing shows that === is to0 specific.
    // eslint-disable-next-line eqeqeq
    if (this.formControl.value == this.initialValue) {
      this.formControl.markAsPristine();
    }
  }

  deepCopy<T>(obj: T): T {
    let copy;

    // Handle the 3 simple types, and null or undefined
    if (null == obj || 'object' !== typeof obj) {
      return obj;
    }

    // Handle Date
    if (obj instanceof Date) {
      copy = new Date();
      copy.setTime(obj.getTime());
      return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
      copy = [];
      for (let i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.deepCopy(obj[i]);
      }
      return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
      copy = {};
      for (const attr in obj) {
        if (obj.hasOwnProperty(attr)) {
          copy[attr] = this.deepCopy(obj[attr]);
        }
      }
      return copy;
    }

    throw new Error("Unable to copy obj! Its type isn't supported.");
  }

  ngOnDestroy(): void {
    this.changeSubscription?.unsubscribe();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  protected optionsChanged(options: ISelectOptionGroup[]) {}
}
